/**
 * DialogOverlay — shared modal overlay for file/folder picker dialogs.
 *
 * Renders a centered modal with backdrop, title, content area, and action buttons.
 * Uses React portal to render at document.body, escaping any parent stacking context.
 */

import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useT } from "@magic-md/i18n/react";
import { cn } from "../../lib/utils";

export interface DialogOverlayProps {
    title: string;
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    footer?: ReactNode;
    /** Dialog width class */
    className?: string;
    /** Prevent dismissal while a non-cancellable action is in flight. */
    closeDisabled?: boolean;
}

export function DialogOverlay({
    title,
    open,
    onClose,
    children,
    footer,
    className,
    closeDisabled = false,
}: DialogOverlayProps) {
    const t = useT("editor");
    const dialogRef = useRef<HTMLDivElement>(null);
    const requestClose = useCallback(() => {
        if (!closeDisabled) onClose();
    }, [closeDisabled, onClose]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") requestClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, requestClose]);

    // Focus trap: cycle focus within the dialog when Tab / Shift-Tab is pressed
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const el = dialogRef.current;
            if (!el) return;
            const focusable = Array.from(
                el.querySelectorAll<HTMLElement>(
                    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                ),
            ).filter((node) => !node.closest("[hidden]"));
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open]);

    // Move focus into dialog when it opens
    useEffect(() => {
        if (!open) return;
        const el = dialogRef.current;
        if (!el) return;
        const focusable = el.querySelector<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        focusable?.focus();
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9990] flex items-center justify-center" data-dialog-portal>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={requestClose}
            />

            {/* Dialog */}
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={cn(
                    "relative bg-background border border-border rounded-lg shadow-xl flex flex-col max-h-[80vh]",
                    className ?? "w-[560px]",
                )}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
                    <h2 className="text-sm font-medium text-foreground">{title}</h2>
                    <button
                        type="button"
                        aria-label={t("common.close")}
                        onClick={requestClose}
                        disabled={closeDisabled}
                        className={cn(
                            "p-1 rounded text-muted-foreground hover:text-foreground",
                            closeDisabled
                                ? "cursor-not-allowed opacity-40"
                                : "hover:bg-muted",
                        )}
                    >
                        <X size={14} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 overflow-hidden">
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}

/* ── Shared button styles ── */

export function DialogButton({
    children,
    onClick,
    variant = "default",
    disabled,
    className: extraClassName,
}: {
    children: ReactNode;
    onClick: () => void;
    variant?: "default" | "primary";
    disabled?: boolean;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
                "px-3 py-1.5 text-xs font-medium rounded transition-colors",
                variant === "primary"
                    ? "bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    : "bg-muted text-foreground hover:bg-muted/80",
                disabled && "cursor-not-allowed opacity-50",
                extraClassName,
            )}
        >
            {children}
        </button>
    );
}
